import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PORTAL_PAGE_TITLE_ID,
} from '@beautyburst/ui';
import { useEffect, useState, type ReactNode } from 'react';
import type {
  AdminApiClient,
  AdminDeliveryAssignment,
  AdminImportJob,
  AdminStoreStatus,
} from '../api-client.ts';
import { adminHref } from '../base-path.ts';
import { AdminApiError } from '../http-client.ts';
import type { RouteContract } from '../routes.ts';

type Remote<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly value: T }
  | { readonly status: 'failed'; readonly error: unknown };

type DeliveryStatus =
  'REQUESTED' | 'DISPATCHED' | 'PICKED_UP' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED' | 'UNAVAILABLE';

const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'REQUESTED',
  'DISPATCHED',
  'PICKED_UP',
  'IN_TRANSIT',
  'DELIVERED',
  'FAILED',
  'UNAVAILABLE',
];

function useRemote<T>(load: () => Promise<T>, key: string): [Remote<T>, () => void] {
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState<Remote<T>>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    void load().then(
      (value) => active && setState({ status: 'loaded', value }),
      (error: unknown) => active && setState({ status: 'failed', error }),
    );
    return () => {
      active = false;
    };
  }, [key, refresh]);
  return [state, () => setRefresh((value) => value + 1)];
}

function description(error: unknown) {
  return error instanceof AdminApiError
    ? error.message
    : 'The administrator service did not return data. Try again.';
}

function Panel({
  title,
  state,
  retry,
  empty,
  children,
}: {
  readonly title: string;
  readonly state: Remote<unknown>;
  readonly retry: () => void;
  readonly empty?: { readonly title: string; readonly description: string };
  readonly children: ReactNode;
}) {
  if (state.status === 'loading') return <LoadingState label={`Loading ${title}`} />;
  if (state.status === 'failed')
    return (
      <ErrorState
        title={`${title} unavailable`}
        description={description(state.error)}
        headingLevel={2}
        retry={
          <Button style={{ minWidth: '44px', minHeight: '44px' }} onClick={retry}>
            Retry request
          </Button>
        }
      />
    );
  if (empty !== undefined)
    return (
      <EmptyState
        title={empty.title}
        description={empty.description}
        headingLevel={2}
        action={
          <Button style={{ minWidth: '44px', minHeight: '44px' }} onClick={retry}>
            Refresh
          </Button>
        }
      />
    );
  return <>{children}</>;
}

function providerLabel(job: Pick<AdminDeliveryAssignment, 'provider'>) {
  if (job.provider.isMock || job.provider.environment === 'MOCK')
    return 'Mock staging provider — no live courier connected';
  if (job.provider.environment === 'SANDBOX') return 'Sandbox provider — no live courier connected';
  return job.provider.displayName ?? job.provider.adapterKind;
}

function DeliveryOperations({ client }: { readonly client: AdminApiClient }) {
  const [status, setStatus] = useState<DeliveryStatus | ''>('');
  const [market, setMarket] = useState('');
  const [offset, setOffset] = useState(0);
  const [applied, setApplied] = useState({ status: '' as DeliveryStatus | '', market: '' });
  const key = `${applied.status}:${applied.market}:${offset}`;
  const [state, retry] = useRemote(
    () =>
      client.delivery.jobs({
        ...(applied.status === '' ? {} : { status: applied.status }),
        ...(applied.market === '' ? {} : { market: applied.market }),
        limit: 25,
        offset,
      }),
    key,
  );
  const [detail, setDetail] = useState<Remote<AdminDeliveryAssignment> | null>(null);
  const inspect = (assignmentId: string) => {
    setDetail({ status: 'loading' });
    void client.delivery.detail(assignmentId).then(
      (value) => setDetail({ status: 'loaded', value }),
      (error: unknown) => setDetail({ status: 'failed', error }),
    );
  };
  const jobs = state.status === 'loaded' ? state.value.assignments : [];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Card>
        <h2>Provider-neutral lifecycle</h2>
        <p>
          Track requested, dispatched, picked up, in transit, delivered, failed, and unavailable
          jobs without assuming a courier partner. Staging uses a mock or sandbox provider; no live
          courier is connected.
        </p>
        <p>
          <strong>UI BUILT — INTEGRATION ACCOUNT REQUIRED</strong> for a live courier connection.
        </p>
      </Card>
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ status, market: market.trim() });
          setOffset(0);
        }}
      >
        <label htmlFor="delivery-status-filter">
          Lifecycle status
          <select
            id="delivery-status-filter"
            value={status}
            onChange={(event) => setStatus(event.currentTarget.value as DeliveryStatus | '')}
          >
            <option value="">All statuses</option>
            {DELIVERY_STATUSES.map((item) => (
              <option key={item} value={item}>
                {item.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <Field
          id="delivery-market-filter"
          label="Market ID"
          value={market}
          onChange={(event) => setMarket(event.currentTarget.value)}
          hint="Leave blank to include all markets."
        />
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Apply filters
        </Button>
      </form>
      <Panel
        title="Delivery jobs"
        state={state}
        retry={retry}
        empty={
          state.status === 'loaded' && jobs.length === 0
            ? {
                title: 'No delivery jobs match these filters',
                description: 'Try another lifecycle status or market, or refresh the queue.',
              }
            : undefined
        }
      >
        {state.status === 'loaded' ? (
          <>
            <DataTable
              caption="Delivery jobs returned by the administrator API"
              rows={jobs}
              rowKey={(job) => job.assignmentId}
              columns={[
                { key: 'assignment', header: 'Assignment', render: (job) => job.assignmentId },
                {
                  key: 'lifecycle',
                  header: 'Lifecycle',
                  render: (job) => job.lifecycleStatus.replaceAll('_', ' '),
                },
                {
                  key: 'market',
                  header: 'Market',
                  render: (job) => job.fulfilment.marketId ?? 'No market recorded',
                },
                {
                  key: 'provider',
                  header: 'Courier integration',
                  render: (job) => providerLabel(job),
                },
                { key: 'order', header: 'Order', render: (job) => job.fulfilment.orderId },
                {
                  key: 'inspect',
                  header: 'Lifecycle history',
                  render: (job) => (
                    <Button
                      aria-label={`Inspect delivery job ${job.assignmentId}`}
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => inspect(job.assignmentId)}
                    >
                      Inspect
                    </Button>
                  ),
                },
              ]}
            />
            <nav className="admin-pagination" aria-label="Delivery job pagination">
              <Button
                variant="secondary"
                disabled={offset === 0}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => Math.max(0, value - 25))}
              >
                Previous page
              </Button>
              <span aria-live="polite">
                Showing jobs {offset + 1}–{offset + jobs.length}
              </span>
              <Button
                variant="secondary"
                disabled={jobs.length < 25}
                style={{ minWidth: '44px', minHeight: '44px' }}
                onClick={() => setOffset((value) => value + 25)}
              >
                Next page
              </Button>
            </nav>
          </>
        ) : null}
      </Panel>
      {detail === null ? null : (
        <section aria-labelledby="delivery-history-heading">
          <h2 id="delivery-history-heading">Delivery job history</h2>
          <Panel
            title="Delivery job history"
            state={detail}
            retry={() => detail.status === 'loaded' && inspect(detail.value.assignmentId)}
            empty={
              detail.status === 'loaded' &&
              detail.value.trackingEvents.length === 0 &&
              detail.value.courierEvents.length === 0
                ? {
                    title: 'No lifecycle events recorded',
                    description:
                      'The current server-derived lifecycle is still shown in the delivery job list.',
                  }
                : undefined
            }
          >
            {detail.status === 'loaded' ? (
              <>
                <dl className="admin-definition-list">
                  <dt>Current lifecycle</dt>
                  <dd>{detail.value.lifecycleStatus.replaceAll('_', ' ')}</dd>
                  <dt>Courier integration</dt>
                  <dd>{providerLabel(detail.value)}</dd>
                  <dt>Assignment reference</dt>
                  <dd>{detail.value.provider.assignmentReference ?? 'Not provided'}</dd>
                </dl>
                <DataTable
                  caption="Delivery tracking events"
                  rows={detail.value.trackingEvents}
                  rowKey={(event) => String(event.id)}
                  emptyMessage="No delivery tracking events are recorded."
                  columns={[
                    { key: 'status', header: 'Status', render: (event) => String(event.status) },
                    {
                      key: 'actor',
                      header: 'Actor',
                      render: (event) => String(event.actor_kind ?? '—'),
                    },
                    {
                      key: 'time',
                      header: 'Occurred',
                      render: (event) => String(event.occurred_at ?? '—'),
                    },
                    { key: 'note', header: 'Note', render: (event) => String(event.note ?? '—') },
                  ]}
                />
                <DataTable
                  caption="Courier integration events"
                  rows={detail.value.courierEvents}
                  rowKey={(event) => String(event.id)}
                  emptyMessage="No courier integration events are recorded."
                  columns={[
                    {
                      key: 'transition',
                      header: 'Transition',
                      render: (event) =>
                        `${String(event.from_status ?? '—')} → ${String(event.to_status ?? '—')}`,
                    },
                    {
                      key: 'integration',
                      header: 'Integration',
                      render: (event) => String(event.integration_display_name ?? '—'),
                    },
                    {
                      key: 'time',
                      header: 'Recorded',
                      render: (event) => String(event.created_at ?? '—'),
                    },
                  ]}
                />
              </>
            ) : null}
          </Panel>
        </section>
      )}
    </section>
  );
}

function DirectInventory({ client }: { readonly client: AdminApiClient }) {
  const [locationId, setLocationId] = useState('');
  const [appliedLocation, setAppliedLocation] = useState('');
  const [state, retry] = useRemote(
    () => client.directInventory.listings(appliedLocation === '' ? undefined : appliedLocation),
    `direct:${appliedLocation}`,
  );
  const listings = state.status === 'loaded' ? state.value : [];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Card>
        <h2>Separate owner, inventory, and settlement</h2>
        <p>
          Beauty Burst Direct is platform-owned inventory. It is never merged with partner-store
          inventory, partner commissions, or partner payout figures.
        </p>
      </Card>
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          setAppliedLocation(locationId.trim());
        }}
      >
        <Field
          id="direct-location-filter"
          label="Location ID"
          value={locationId}
          onChange={(event) => setLocationId(event.currentTarget.value)}
          hint="Optional Direct inventory location filter."
        />
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Apply filter
        </Button>
      </form>
      <Panel
        title="Direct inventory"
        state={state}
        retry={retry}
        empty={
          state.status === 'loaded' && listings.length === 0
            ? {
                title: 'Beauty Burst Direct has no live inventory',
                description:
                  'The Direct rail is ready for platform-owned listings. Partner-store stock, commissions, and payouts remain separate.',
              }
            : undefined
        }
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Beauty Burst Direct listings only"
            rows={listings}
            rowKey={(listing) => String(listing.id)}
            columns={[
              { key: 'listing', header: 'Listing', render: (listing) => String(listing.id) },
              {
                key: 'product',
                header: 'Product',
                render: (listing) => String(listing.product_name ?? listing.productName ?? '—'),
              },
              {
                key: 'location',
                header: 'Location',
                render: (listing) =>
                  String(
                    listing.location_name ?? listing.locationName ?? listing.location_id ?? '—',
                  ),
              },
              {
                key: 'on-hand',
                header: 'On hand',
                render: (listing) => String(listing.on_hand ?? '—'),
              },
              {
                key: 'reserved',
                header: 'Reserved',
                render: (listing) => String(listing.reserved ?? '—'),
              },
              {
                key: 'status',
                header: 'Inventory status',
                render: (listing) => String(listing.inventory_status ?? '—'),
              },
            ]}
          />
        ) : null}
      </Panel>
    </section>
  );
}

function StoresOversight({ client }: { readonly client: AdminApiClient }) {
  const [market, setMarket] = useState('');
  const [status, setStatus] = useState<AdminStoreStatus | ''>('');
  const [applied, setApplied] = useState({ market: '', status: '' as AdminStoreStatus | '' });
  const [state, retry] = useRemote(
    () =>
      client.stores.list({
        ...(applied.market === '' ? {} : { market: applied.market }),
        ...(applied.status === '' ? {} : { status: applied.status }),
      }),
    `stores:${applied.market}:${applied.status}`,
  );
  const locations = state.status === 'loaded' ? state.value : [];
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Card>
        <h2>Approved retailer locations</h2>
        <p>
          Current store and extended-hours availability is composed by the server. This screen
          displays that authoritative state without calculating operating availability in the
          browser.
        </p>
      </Card>
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ market: market.trim(), status });
        }}
      >
        <Field
          id="stores-market-filter"
          label="Market ID"
          value={market}
          onChange={(event) => setMarket(event.currentTarget.value)}
          hint="Leave blank to include all markets."
        />
        <label htmlFor="stores-status-filter">
          Store status
          <select
            id="stores-status-filter"
            value={status}
            onChange={(event) => setStatus(event.currentTarget.value as AdminStoreStatus | '')}
          >
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="PAUSED">Paused</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="CLOSED">Closed</option>
          </select>
        </label>
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Apply filters
        </Button>
      </form>
      <Panel
        title="Stores"
        state={state}
        retry={retry}
        empty={
          state.status === 'loaded' && locations.length === 0
            ? {
                title: 'No approved stores match these filters',
                description: 'Try another market or status, or refresh the current location list.',
              }
            : undefined
        }
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Approved retailer locations with server-composed availability"
            rows={locations}
            rowKey={(store) => store.location.id}
            columns={[
              {
                key: 'retailer',
                header: 'Retailer',
                render: (store) => (
                  <a
                    href={adminHref(
                      `/retailers?retailerId=${encodeURIComponent(store.retailer.id)}`,
                    )}
                  >
                    {store.retailer.name}
                  </a>
                ),
              },
              { key: 'location', header: 'Location', render: (store) => store.location.name },
              { key: 'status', header: 'Store status', render: (store) => store.location.status },
              {
                key: 'market',
                header: 'Market',
                render: (store) => store.market?.name ?? 'No market assigned',
              },
              {
                key: 'availability',
                header: 'Server-composed availability',
                render: (store) => store.availability.labelText,
              },
              {
                key: 'withheld',
                header: 'Availability notes',
                render: (store) =>
                  store.availability.withheldReasons.length === 0
                    ? 'No availability holds'
                    : store.availability.withheldReasons.join(', '),
              },
              {
                key: 'detail',
                header: 'Location detail',
                render: (store) => (
                  <details>
                    <summary>View location details</summary>
                    <dl className="admin-definition-list">
                      <dt>Retailer ID</dt>
                      <dd>{store.retailer.id}</dd>
                      <dt>Location ID</dt>
                      <dd>{store.location.id}</dd>
                      <dt>Market ID</dt>
                      <dd>{store.market?.id ?? 'No market assigned'}</dd>
                      <dt>Composer label</dt>
                      <dd>{store.availability.label}</dd>
                    </dl>
                  </details>
                ),
              },
            ]}
          />
        ) : null}
      </Panel>
    </section>
  );
}

function ImportOversight({ client }: { readonly client: AdminApiClient }) {
  const [retailerId, setRetailerId] = useState('');
  const [status, setStatus] = useState('');
  const [quarantine, setQuarantine] = useState<'HELD' | 'CLEAR' | ''>('');
  const [applied, setApplied] = useState({
    retailerId: '',
    status: '',
    quarantine: '' as 'HELD' | 'CLEAR' | '',
  });
  const [releaseTarget, setReleaseTarget] = useState<AdminImportJob | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseOutcome, setReleaseOutcome] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [state, retry] = useRemote(
    () =>
      client.imports.jobs({
        ...(applied.retailerId === '' ? {} : { retailerId: applied.retailerId }),
        ...(applied.status === '' ? {} : { status: applied.status }),
        ...(applied.quarantine === '' ? {} : { quarantine: applied.quarantine }),
        limit: 25,
        offset: 0,
      }),
    `imports:${applied.retailerId}:${applied.status}:${applied.quarantine}`,
  );
  const jobs = state.status === 'loaded' ? state.value.jobs : [];
  const release = () => {
    if (releaseTarget === null) return;
    setReleasing(true);
    setReleaseError(null);
    void client.imports.releaseQuarantine(releaseTarget.id).then(
      () => {
        setReleaseOutcome('Quarantine release completed and recorded in the audit log.');
        setReleaseTarget(null);
        setReleasing(false);
        retry();
      },
      (error: unknown) => {
        setReleaseError(description(error));
        setReleasing(false);
      },
    );
  };
  return (
    <section aria-labelledby={PORTAL_PAGE_TITLE_ID}>
      <Card>
        <h2>File scan and quarantine controls</h2>
        <p>
          Release is limited to held files and is a privileged, audit-recorded administrator action.
          Scan settings are not shown because the domain does not provide an administrator
          scan-settings contract.
        </p>
      </Card>
      <form
        className="admin-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({
            retailerId: retailerId.trim(),
            status: status.trim(),
            quarantine,
          });
        }}
      >
        <Field
          id="imports-retailer-filter"
          label="Retailer ID"
          value={retailerId}
          onChange={(event) => setRetailerId(event.currentTarget.value)}
          hint="Leave blank to include all retailers."
        />
        <Field
          id="imports-status-filter"
          label="Job status"
          value={status}
          onChange={(event) => setStatus(event.currentTarget.value)}
          hint="For example, MAPPING or PUBLISHED."
        />
        <label htmlFor="imports-quarantine-filter">
          Quarantine state
          <select
            id="imports-quarantine-filter"
            value={quarantine}
            onChange={(event) => setQuarantine(event.currentTarget.value as 'HELD' | 'CLEAR' | '')}
          >
            <option value="">All states</option>
            <option value="HELD">Held</option>
            <option value="CLEAR">Clear</option>
          </select>
        </label>
        <Button type="submit" style={{ minWidth: '44px', minHeight: '44px' }}>
          Apply filters
        </Button>
      </form>
      {releaseOutcome === null ? null : <p role="status">{releaseOutcome}</p>}
      {releaseError === null ? null : <p role="alert">{releaseError}</p>}
      <Panel
        title="Import jobs"
        state={state}
        retry={retry}
        empty={
          state.status === 'loaded' && jobs.length === 0
            ? {
                title: 'No import jobs match these filters',
                description: 'No scan or quarantine records matched the selected filters.',
              }
            : undefined
        }
      >
        {state.status === 'loaded' ? (
          <DataTable
            caption="Import jobs with file scan and quarantine state"
            rows={jobs}
            rowKey={(job) => job.id}
            columns={[
              {
                key: 'retailer',
                header: 'Retailer',
                render: (job) => job.retailer.name,
              },
              { key: 'location', header: 'Location', render: (job) => job.location.name },
              {
                key: 'file',
                header: 'File',
                render: (job) => job.file.originalName ?? 'File name not recorded',
              },
              { key: 'job-status', header: 'Job status', render: (job) => job.job.status },
              {
                key: 'scan',
                header: 'Scan state',
                render: (job) => job.file.scanStatus ?? 'Scan state not recorded',
              },
              {
                key: 'quarantine',
                header: 'Quarantine state',
                render: (job) => job.processing.quarantineStatus,
              },
              {
                key: 'hold-reason',
                header: 'Hold reason',
                render: (job) => job.processing.holdReason ?? 'No hold reason recorded',
              },
              {
                key: 'release',
                header: 'Security action',
                render: (job) =>
                  job.processing.quarantineStatus === 'HELD' ? (
                    <Button
                      aria-label={`Release quarantine for ${job.file.originalName ?? job.id}`}
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => {
                        setReleaseTarget(job);
                        setReleaseError(null);
                      }}
                    >
                      Release quarantine
                    </Button>
                  ) : (
                    'No release required'
                  ),
              },
            ]}
          />
        ) : null}
      </Panel>
      {releaseTarget === null ? null : (
        <Card>
          <section
            aria-labelledby="release-quarantine-confirmation"
            role="alertdialog"
            aria-modal="true"
          >
            <h2 id="release-quarantine-confirmation">Confirm quarantine release</h2>
            <p>
              Release {releaseTarget.file.originalName ?? 'this import file'} for{' '}
              {releaseTarget.retailer.name}? This clears the hold and is recorded in the audit log.
            </p>
            <Button
              variant="secondary"
              disabled={releasing}
              style={{ minWidth: '44px', minHeight: '44px' }}
              onClick={() => setReleaseTarget(null)}
            >
              Cancel
            </Button>{' '}
            <Button
              disabled={releasing}
              style={{ minWidth: '44px', minHeight: '44px' }}
              onClick={release}
            >
              {releasing ? 'Releasing quarantine…' : 'Confirm release'}
            </Button>
          </section>
        </Card>
      )}
    </section>
  );
}

export function OperationsScreen({
  route,
  client,
}: {
  readonly route: RouteContract;
  readonly client: AdminApiClient;
  readonly flags?: Readonly<Record<string, boolean>>;
}) {
  switch (route.path) {
    case '/delivery-operations':
      return <DeliveryOperations client={client} />;
    case '/direct-inventory':
      return <DirectInventory client={client} />;
    case '/stores':
      return <StoresOversight client={client} />;
    case '/imports':
      return <ImportOversight client={client} />;
    default:
      return null;
  }
}
